import * as z from 'zod'

type ContentfulEntry = {
  readonly fields: object
  readonly sys: {
    readonly contentType: {
      readonly sys: {
        readonly id: string
      }
    }
  }
}

type EntryOfContentType<Entry, ContentType extends string> = Extract<
  Entry,
  {readonly sys: {readonly contentType: {readonly sys: {readonly id: ContentType}}}}
>

export class BrandContentError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'BrandContentError'
  }
}

export function findByContentType<
  Entry extends ContentfulEntry,
  const ContentType extends Entry['sys']['contentType']['sys']['id'],
>(entries: readonly Entry[] | undefined, contentType: ContentType): EntryOfContentType<Entry, ContentType> | undefined {
  return entries?.find(entry => entry.sys.contentType.sys.id === contentType) as
    | EntryOfContentType<Entry, ContentType>
    | undefined
}

export function findAllByContentType<
  Entry extends ContentfulEntry,
  const ContentType extends Entry['sys']['contentType']['sys']['id'],
>(entries: readonly Entry[] | undefined, contentType: ContentType): Array<EntryOfContentType<Entry, ContentType>> {
  return (entries ?? []).filter(entry => entry.sys.contentType.sys.id === contentType) as Array<
    EntryOfContentType<Entry, ContentType>
  >
}

export function findById<Entry extends ContentfulEntry>(
  entries: readonly Entry[] | undefined,
  id: string,
): Entry | undefined {
  const matches = (entries ?? []).filter(entry => getEntryId(entry) === id)

  if (matches.length > 1) {
    throw new BrandContentError(`Duplicate content id "${id}": found ${matches.length} entries`)
  }

  return matches[0]
}

export function findByIdWithSchema<Entry extends ContentfulEntry, Schema extends z.ZodType>(
  entries: readonly Entry[] | undefined,
  id: string,
  schema: Schema,
): z.output<Schema> | undefined {
  const entry = findById(entries, id)

  if (!entry) {
    return undefined
  }

  return parseEntry(entry, id, schema)
}

export function requireById<Entry extends ContentfulEntry, Schema extends z.ZodType>(
  entries: readonly Entry[] | undefined,
  id: string,
  schema: Schema,
): z.output<Schema> {
  const entry = findById(entries, id)

  if (!entry) {
    throw new BrandContentError(`Required content id "${id}" was not found`)
  }

  return parseEntry(entry, id, schema)
}

export function parseEntries<Schema extends z.ZodType>(
  entries: readonly unknown[] | undefined,
  schema: Schema,
): Array<z.output<Schema>> {
  const result = z.array(schema).safeParse(entries ?? [])

  if (!result.success) {
    throw new BrandContentError(`Content entries did not match the expected schema:\n${z.prettifyError(result.error)}`)
  }

  return result.data
}

export function findDuplicateIds<Entry extends ContentfulEntry>(entries: readonly Entry[] | undefined): string[] {
  const counts = new Map<string, number>()

  for (const entry of entries ?? []) {
    const id = getEntryId(entry)

    if (id !== undefined) {
      counts.set(id, (counts.get(id) ?? 0) + 1)
    }
  }

  return [...counts.entries()]
    .filter(([, count]) => count > 1)
    .map(([id]) => id)
    .sort()
}

export function assertUniqueIds<Entry extends ContentfulEntry>(entries: readonly Entry[] | undefined): void {
  const duplicateIds = findDuplicateIds(entries)

  if (duplicateIds.length > 0) {
    throw new BrandContentError(`Duplicate content ids: ${duplicateIds.map(id => `"${id}"`).join(', ')}`)
  }
}

function parseEntry<Schema extends z.ZodType>(entry: ContentfulEntry, id: string, schema: Schema): z.output<Schema> {
  const result = schema.safeParse(entry)

  if (!result.success) {
    throw new BrandContentError(
      `Content id "${id}" did not match the expected schema:\n${z.prettifyError(result.error)}`,
    )
  }

  return result.data
}

function getEntryId(entry: ContentfulEntry): string | undefined {
  if (!('id' in entry.fields)) {
    return undefined
  }

  return typeof entry.fields.id === 'string' ? entry.fields.id : undefined
}
